/** biome-ignore-all lint/complexity/noStaticOnlyClass: Utility class for CAIP building */
import { CHAIN_AGNOSTIC_REFERENCE } from "./constants";
import { CAIPValidationError } from "./errors";
import type {
  AccountAddress,
  AccountId,
  AssetId,
  AssetNamespace,
  AssetReference,
  ChainId,
  ChainNamespace,
  ChainReference,
} from "./types";
import {
  normalizeAccountAddress,
  normalizeAssetReference,
} from "./normalization";
import { Validator } from "./validator";

/**
 * CAIP Builder
 * Provides static builder methods for constructing CAIP identifiers
 */
export class Builder {
  /**
   * Builds a Chain CAIP from components
   * @param chain - The chain components to build the CAIP from
   * @throws {CAIPValidationError} if any component is invalid
   */
  static buildChainId(chain: {
    namespace: ChainNamespace;
    reference: ChainReference;
  }): ChainId {
    const { namespace, reference } = chain;
    if (!Validator.isValidChainNamespace(namespace)) {
      throw new CAIPValidationError(
        `Invalid chain namespace: ${namespace}. Must be one of: eip155, solana, cosmos, starknet, sui`,
        namespace,
      );
    }

    if (!Validator.isValidChainReference(reference)) {
      throw new CAIPValidationError(
        `Invalid chain reference: ${reference}. Must match pattern: [-_a-zA-Z0-9]{1,32}`,
        reference,
      );
    }

    return (namespace + ":" + reference) as ChainId;
  }

  /**
   * Builds a CAIP-10 Account ID from chain + account components.
   *
   * The address is normalized via {@link normalizeAccountAddress}: EVM
   * addresses are lowercased; Solana base58 is left untouched (case is
   * load-bearing). Components are validated against
   * {@link Validator.isValidChainNamespace}, {@link Validator.isValidChainReference},
   * and {@link Validator.isValidAccountAddress}.
   *
   * **Default references.** When `chain.reference` is `null` or `undefined`,
   * the builder falls back to the per-namespace value defined in
   * {@link CHAIN_AGNOSTIC_REFERENCE}. For `eip155` this is the chain-agnostic
   * `"0"` reference; for `sui` it is the canonical `"mainnet"` reference.
   *
   * Namespaces without a default (Solana — every account is bound to a
   * specific cluster) raise a {@link CAIPValidationError} when called without
   * an explicit `reference`. Pass the cluster reference explicitly for those
   * namespaces.
   *
   * @param chain - Chain components.
   * @param chain.namespace - CAIP-2 chain namespace (`eip155`, `solana`, ...).
   * @param chain.reference - CAIP-2 chain reference. Pass `null` (or
   *   omit) to use the namespace's default — only valid
   *   for namespaces present in {@link CHAIN_AGNOSTIC_REFERENCE}.
   * @param account - Account components.
   * @param account.address - Raw account address. Will be normalized by
   *   namespace-aware rules.
   * @returns A canonical CAIP-10 string of the form
   *   `<namespace>:<reference>:<normalizedAddress>`.
   * @throws {CAIPValidationError} If the namespace, reference, or
   *   address fails validation, or if `reference` is omitted for a
   *   namespace that has no default reference.
   *
   * @example
   * // Chain-bound EVM smart account (Kernel vault on Base)
   * Builder.buildAccountId(
   *   { namespace: "eip155", reference: "8453" },
   *   { address: "0xe3a2d1f49aee887e42655b56371d4d76bbf58058" },
   * );
   * // → "eip155:8453:0xe3a2d1f49aee887e42655b56371d4d76bbf58058"
   *
   * @example
   * // Chain-agnostic EVM EOA — reference defaults to "0"
   * Builder.buildAccountId(
   *   { namespace: "eip155", reference: null },
   *   { address: "0xABCDEF0000000000000000000000000000000001" },
   * );
   * // → "eip155:0:0xabcdef0000000000000000000000000000000001"
   */
  static buildAccountId(
    chain: {
      namespace: ChainNamespace;
      reference?: ChainReference | null;
    },
    account: {
      address: AccountAddress;
    },
  ): AccountId {
    // Resolve the namespace default when caller omits the reference.
    let resolvedReference: ChainReference;
    if (chain.reference == null) {
      const fallback = CHAIN_AGNOSTIC_REFERENCE[chain.namespace];
      if (fallback == null) {
        throw new CAIPValidationError(
          `Chain namespace '${chain.namespace}' has no chain-agnostic CAIP-10 reference; pass an explicit chain reference.`,
          chain.namespace,
        );
      }
      resolvedReference = fallback;
    } else {
      resolvedReference = chain.reference;
    }

    const chainId = Builder.buildChainId({
      namespace: chain.namespace,
      reference: resolvedReference,
    });

    if (!Validator.isValidAccountAddress(account.address)) {
      throw new CAIPValidationError(
        `Invalid account address: ${account.address}. Must match pattern: [-.%a-zA-Z0-9]{1,128}`,
        account.address,
      );
    }

    return (chainId +
      ":" +
      normalizeAccountAddress(chain.namespace, account.address)) as AccountId;
  }

  /**
   * Builds an Asset ID from components
   * @throws {CAIPValidationError} if any component is invalid
   */
  static buildAssetId(
    chain: {
      namespace: ChainNamespace;
      reference: ChainReference;
    },
    asset: {
      namespace: AssetNamespace;
      reference: AssetReference;
    },
  ): AssetId {
    const chainId = Builder.buildChainId(chain);

    if (!Validator.isValidAssetNamespace(asset.namespace)) {
      throw new CAIPValidationError(
        `Invalid asset namespace: ${asset.namespace}. Must be one of: erc20, slip44, spl, ibc, erc721, erc1155, coin`,
        asset.namespace,
      );
    }

    if (
      !Validator.isValidAssetNamespaceForChain(chain.namespace, asset.namespace)
    ) {
      throw new CAIPValidationError(
        `Invalid chain/asset namespace pairing: ${chain.namespace}/${asset.namespace}. The coin namespace is supported only on Sui, and Sui assets must use coin.`,
        `${chain.namespace}/${asset.namespace}`,
      );
    }

    if (
      !Validator.isValidAssetReference(asset.reference, asset.namespace)
    ) {
      throw new CAIPValidationError(
        `Invalid asset reference: ${asset.reference}`,
        asset.reference,
      );
    }

    return (chainId +
      "/" +
      asset.namespace +
      ":" +
      normalizeAssetReference(chain.namespace, asset.reference)) as AssetId;
  }
}
